import type { ComponentPropsWithoutRef, ElementType } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders an assistant reply written in Markdown (headings, bold, lists, GFM tables, links) as formatted text.
 *
 * Plain text goes in and React elements come out: there is no `dangerouslySetInnerHTML`, raw HTML in the text is
 * dropped (`skipHtml`), and react-markdown's default URL transform removes `javascript:` style links. Links open in
 * a new tab with `rel="noopener noreferrer"`. The text is never logged or sent anywhere, so it is as private as the
 * page that shows it (presentation-mode masking is the caller's job, applied to the string before it gets here).
 */

type Tag<T extends ElementType> = Omit<ComponentPropsWithoutRef<T>, 'ref'> & { node?: unknown };

export interface MarkdownMessageProps {
  /** The Markdown source. Null and undefined render nothing. */
  children: string | null | undefined;
  /** Extra classes for the wrapper, for example the text colour of the bubble it sits in. */
  className?: string;
}

/** Leading Markdown marks (`## `, `# `, `**`, `> `, list markers) removed, for titles and one-line previews. */
export function plainTextFromMarkdown(value: string | null | undefined): string {
  return (value ?? '')
    .replace(/^\s*(?:#{1,6}\s+|>\s+|[-*+]\s+|\d+[.)]\s+)+/, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*\*/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

export function MarkdownMessage({ children, className = '' }: MarkdownMessageProps) {
  const source = children || '';
  if (!source.trim()) return null;

  return (
    <div className={`prose prose-sm max-w-none break-words ${className}`} data-testid="markdown-message">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          a: ({ node, ...props }: Tag<'a'>) => {
            void node;
            return <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline underline-offset-2 hover:text-blue-900" />;
          },
          h1: ({ node, ...props }: Tag<'h1'>) => {
            void node;
            return <h1 {...props} className="mb-2 mt-3 text-lg font-semibold first:mt-0" />;
          },
          h2: ({ node, ...props }: Tag<'h2'>) => {
            void node;
            return <h2 {...props} className="mb-2 mt-3 text-base font-semibold first:mt-0" />;
          },
          h3: ({ node, ...props }: Tag<'h3'>) => {
            void node;
            return <h3 {...props} className="mb-1.5 mt-3 text-sm font-semibold first:mt-0" />;
          },
          h4: ({ node, ...props }: Tag<'h4'>) => {
            void node;
            return <h4 {...props} className="mb-1 mt-2 text-sm font-semibold first:mt-0" />;
          },
          p: ({ node, ...props }: Tag<'p'>) => {
            void node;
            return <p {...props} className="mb-2 leading-6 last:mb-0" />;
          },
          strong: ({ node, ...props }: Tag<'strong'>) => {
            void node;
            return <strong {...props} className="font-semibold" />;
          },
          ul: ({ node, ...props }: Tag<'ul'>) => {
            void node;
            return <ul {...props} className="my-2 list-disc space-y-1 pl-5" />;
          },
          ol: ({ node, ...props }: Tag<'ol'>) => {
            void node;
            return <ol {...props} className="my-2 list-decimal space-y-1 pl-5" />;
          },
          blockquote: ({ node, ...props }: Tag<'blockquote'>) => {
            void node;
            return <blockquote {...props} className="my-2 border-l-4 border-gray-300 pl-3 text-gray-600" />;
          },
          hr: ({ node, ...props }: Tag<'hr'>) => {
            void node;
            return <hr {...props} className="my-3 border-gray-200" />;
          },
          pre: ({ node, ...props }: Tag<'pre'>) => {
            void node;
            return <pre {...props} className="my-2 overflow-x-auto rounded-lg bg-gray-950 p-3 text-xs text-gray-50" />;
          },
          code: ({ node, className: codeClassName, children: codeChildren, ...props }: Tag<'code'>) => {
            void node;
            return (
              <code {...props} className={`rounded bg-gray-100 px-1 py-0.5 text-[0.85em] text-gray-900 ${codeClassName || ''}`}>
                {codeChildren}
              </code>
            );
          },
          table: ({ node, ...props }: Tag<'table'>) => {
            void node;
            return (
              <div className="my-2 max-w-full overflow-x-auto">
                <table {...props} className="min-w-full border-collapse text-left text-xs" />
              </div>
            );
          },
          thead: ({ node, ...props }: Tag<'thead'>) => {
            void node;
            return <thead {...props} className="bg-gray-50" />;
          },
          th: ({ node, ...props }: Tag<'th'>) => {
            void node;
            return <th {...props} className="border border-gray-200 px-2 py-1 font-semibold text-gray-900" />;
          },
          td: ({ node, ...props }: Tag<'td'>) => {
            void node;
            return <td {...props} className="border border-gray-200 px-2 py-1 align-top" />;
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownMessage;
